import './index.scss'
import '../../assets/scss/tabs/index.scss'
import Footer from '../../components/layouts/footer'
import {EditIcon, HorizontalMoreIcon, PlusIcon, TrashIcon} from '../../icons'
import {ImgButton} from '../../components/core/button'
import {PageSubTitle, PageTitle, ResponsivePageTitle} from '../../components/core/title'
import {useDispatch, useSelector} from 'react-redux'
import {deleteService, getServices} from '../../stores/resources'
import {setConfirmIsActive} from '../../stores/alert'
import EditPopup from './EditPopup'
import AddPopup from './AddPopup'
import ContentLoader from '../../components/core/loader'
import {useEffect, useState} from 'react'
import Table from '../../components/core/table'
import Confirm from '../../components/core/confirm'
import Modal from '../../components/core/modal'
import Dropdown from '../../components/core/dropdown'
import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import Badge from '../../components/core/badge'
import TeamSwitcher from '../../components/pages/teams'
import Customer from '../../utils/customer'
import {colors} from '../../utils/colors'
import classNames from 'classnames'
import {sortBySelected} from '../../utils/sorting'
import {getAllLimitUsages} from '../../stores/auth'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../utils/env'

const SORT_ID = 'services'

const {isIntegrationEnabled, isReadOnlyModeEnabled} = envConfig

const Services = () => {
    const [checkedList, setCheckedList] = useState([])
    const text = useNamespaceTranslations('services')
    const [addPopupIsActive, setAddPopupIsActive] = useState(false)
    const [editPopupIsActive, setEditPopupIsActive] = useState(false)
    const isFetching = useSelector(state => state.resources.isFetching)
    const isDeleting = useSelector(state => state.resources.isDeleting)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const services = useSelector(state => state.resources.services)
    const [sortedData, setSortedData] = useState([services])
    const service = sortedData?.find(item => item.value === checkedList[0])
    const [clickedService, setClickedService] = useState(null)

    const tableHeader = [
        {
            label: text.service_name,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'name_asc',
                    field: ['name']
                },
                desc: {
                    key: 'name_desc',
                    field: ['name']
                }
            }
        },
        {
            label: text.resource_count,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'resource_count_asc',
                    field: ['resource_count']
                },
                desc: {
                    key: 'resource_count_desc',
                    field: ['resource_count']
                }
            }
        },
        {
            label: text.heartbeat_count,
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'heartbeat_count_asc',
                    field: ['heartbeat_count']
                },
                desc: {
                    key: 'heartbeat_count_desc',
                    field: ['heartbeat_count']
                }
            }
        }
    ]

    const tbodyData = []
    const dispatch = useDispatch()

    const getActionButtonsStatus = () => {
        if (checkedList?.length !== 1) {
            return true
        }
    }

    const handleAddButtonOnclick = () => {
        setAddPopupIsActive(!addPopupIsActive)
    }

    const handleEditButtonOnclick = () => {
        setEditPopupIsActive(!editPopupIsActive)
    }

    const deleteCheckedService = async() => {
        await dispatch(deleteService(clickedService?.value || service?.value))
        dispatch(setConfirmIsActive(false))
        setCheckedList([])
        setClickedService(null)
    }

    const getActionButton = data => {
        return <Dropdown menuButton={<button className={classNames('table-action-btn', {
            dark: isDarkMode
        })}><HorizontalMoreIcon/></button>}>
            <MenuItem onClick={() => {
                setClickedService(data)
                setEditPopupIsActive(true)
            }
            } disabled={isReadOnlyModeEnabled}>
                <span className="d-flex"><EditIcon/></span>
                <span>{text.edit}</span>
            </MenuItem>
            <MenuDivider/>
            <MenuItem className="danger" onClick={() => {
                setClickedService(data)
                dispatch(setConfirmIsActive(true))
            }
            } disabled={isReadOnlyModeEnabled}>
                <span className="d-flex"><TrashIcon stroke={colors.colorRed}/></span>
                <span>{text.delete}</span>
            </MenuItem>
        </Dropdown>
    }

    const openConfirm = () => {
        setClickedService(null)
        dispatch(setConfirmIsActive(true))
    }

    const getBody = () => {
        return (
            isFetching ? (
                <ContentLoader/>
            ) : (
                <>
                    <TeamSwitcher/>
                    {Customer.isOwnTeam() && (
                        <div className="search-filter-actions">
                            <div className="selected-actions">
                                <ImgButton className={classNames('responsive-add-button', {
                                    'light-bg': !isDarkMode
                                })}
                                onClick={handleAddButtonOnclick}
                                disabled={isReadOnlyModeEnabled}>
                                    <PlusIcon stroke={colors.colorMuted}/>
                                    <span className="button-text">{text.new}</span>
                                </ImgButton>
                                <ImgButton className={classNames({
                                    'light-bg': !isDarkMode
                                })}
                                disabled={getActionButtonsStatus() || isReadOnlyModeEnabled}
                                onClick={handleEditButtonOnclick}>
                                    <span><EditIcon/></span>
                                    <span className="button-text">{text.edit}</span>
                                </ImgButton>
                                <ImgButton className={classNames('outline-red', {
                                    'light-bg': !isDarkMode
                                })}
                                disabled={getActionButtonsStatus() || isReadOnlyModeEnabled}
                                onClick={openConfirm}>
                                    <span><TrashIcon stroke={colors.colorRed}/></span>
                                    <span className="button-text">{text.delete}</span>
                                </ImgButton>
                            </div>
                        </div>
                    )}
                    <div className="content-body">
                        <Table
                            strech
                            tableHeader={tableHeader}
                            tbodyData={tbodyData}
                            data={sortedData}
                            identificator='value'
                            inputName='service'
                            hasCheckbox={Customer.isOwnTeam()}
                            isMultiCheckable={false}
                            selectedData={checkedList}
                            setSelectedData={data => setCheckedList(data)}
                            isSortingEnabled
                            setData={setSortedData}
                            sortId={SORT_ID}
                            originalData={services}
                        />
                    </div>
                </>
            )
        )
    }

    useEffect(() => {
        dispatch(getServices(true))
        dispatch(getAllLimitUsages())
    }, [])

    useEffect(() => {
        setSortedData(sortBySelected(services, SORT_ID, tableHeader))
    }, [services])

    sortedData?.map(service => {
        tbodyData.push([
            <span className="min-width">{service?.label}</span>,
            <span className="min-width"><Badge color="primary">{service?.resource_count}</Badge></span>,
            <span className="flex-1">
                <span><Badge color="purple">{service?.heartbeat_count}</Badge></span>
                {Customer.isOwnTeam() && <span>{getActionButton(service)}</span>}
            </span>
        ])
    })

    const closeEditModal = () => {
        setClickedService(null)
        setEditPopupIsActive(false)
    }

    return (
        <>
            <main className={classNames('main services', {
                dark: isDarkMode
            })}>
                <Confirm
                    body={text.are_you_sure}
                    callback={deleteCheckedService}
                    showLoader={isDeleting}
                />
                <Modal closeModal={() => setAddPopupIsActive(false)} isOpen={addPopupIsActive}
                    title={text.add_service}>
                    <AddPopup closeModal={() => setAddPopupIsActive(false)}/>
                </Modal>
                <Modal closeModal={closeEditModal} isOpen={editPopupIsActive}
                    title={text.update_service_name}>
                    <EditPopup service={clickedService || service}
                        setClickedService={setClickedService}
                        closeModal={closeEditModal}/>
                </Modal>
                <div>
                    <ResponsivePageTitle/>
                    <div className="content-title">
                        <div className="left-side">
                            <div>
                                <PageSubTitle>
                                    <p className={classNames('section', {
                                        dark: isDarkMode
                                    })}>{isIntegrationEnabled ? text.section :  text.teams}</p>
                                </PageSubTitle>
                                <PageTitle>
                                    <h1  className={classNames('page-title', {
                                        dark: isDarkMode
                                    })}>{text.title}</h1>
                                </PageTitle>
                            </div>
                        </div>
                        {Customer.isOwnTeam() &&
                            <ImgButton onClick={handleAddButtonOnclick} disabled={isReadOnlyModeEnabled}>
                                {text.add_service}
                                <span><PlusIcon/></span>
                            </ImgButton>
                        }
                    </div>
                    {getBody()}
                </div>
                <Footer/>
            </main>
        </>
    )
}

export default Services
